import { DateTime } from 'luxon';
import { Plan_SubSlot, Plan_Workout, TrainingProgramBackup } from './fitness-plan';
import { isSplitSubSlotId, Log_Workout } from './log-workout';
import { deepClone } from '../deep-helpers';

/**
 * #4587 — putting a training plan back together after it lost every exercise.
 *
 * A customer's plan ended up marked as fully built while holding no exercises at all.
 * The app went on treating it as finished, so every workout it put on her calendar was
 * blank and she had no way out but deleting all her data. What emptied it was never
 * established, so the repair is written to work from whatever the app happens to have
 * kept, rather than from any one assumed cause.
 *
 * This module is pure — plain objects in, plain objects out, no database and no Angular
 * — so every rule below can be unit-tested on its own. The reading of documents and the
 * writing of the repaired plan live in `UserDb.loadPlanAsync`.
 *
 * Why a separate file rather than `fitness-plan.ts`: this needs `Log_Workout`, and
 * `log-workout.ts` already imports from `fitness-plan.ts`. Putting it there would make
 * the two files import each other.
 */

/** Where a candidate copy of the training structure came from. Reported to telemetry so
 *  a later reader can tell which source actually saved the plan. */
export enum PlanRecoverySource {
  /** a `log/<ts>-FitnessPlan` document found by scanning the backups newest-first.
   *
   *  This also covers the backup a weekly checkup names in its own `fitnessPlanBackupId`
   *  — the snapshot taken right before that checkup overwrote the plan. Following that id
   *  separately was considered and is not needed: such a backup is a copy of a live,
   *  finished plan, so the `planBackupById` view indexes it like any other and the scan
   *  reaches it, dated the same. The scan also finds backups no checkup ever pointed at,
   *  and needs no second database index to do it. */
  ScannedBackup = 'scanned_backup',
  /** reassembled from the user's own logged workouts */
  WorkoutLogs = 'workout_logs',
  /** the copy the plan carries of the last generated programme */
  GeneratedPlanBackup = 'generated_plan_backup',
}

/**
 * The one instant two candidates are compared on, as a string that sorts correctly.
 *
 * The timestamps reaching this module are NOT all in the same form. A plan backup's is
 * taken verbatim out of its document id, and this app has written those ids from a
 * device's local time for years — `log/2025-09-14T14:59:55.567+02:00-FitnessPlan` is a
 * real id from a production database. A workout log's `time_utc` can carry the same
 * offset form. Comparing those as raw text puts a local wall-clock reading up against a
 * UTC one, so the older copy can win by as much as the offset — and since the winner is
 * saved, the better copy is then out of reach for good.
 *
 * Answers `undefined` for anything it cannot read as a time, which sorts the candidate
 * last rather than letting an unreadable value win a comparison.
 */
function comparableInstant(timestamp: string | undefined): string | undefined {
  if (!timestamp) return undefined;
  const parsed = DateTime.fromISO(timestamp, { zone: 'utc' });
  return parsed.isValid ? parsed.toISO() : undefined;
}

/** One possible way to put the training structure back. */
export interface PlanRecoveryCandidate extends TrainingProgramBackup {
  source: PlanRecoverySource;
  /** when this copy was made, ISO8601 UTC. Absent for `GeneratedPlanBackup`, which
   *  carries no date of its own — see `pickBestPlanRecoveryCandidate`. */
  timestampUtc?: string;
}

/** Does this training structure actually hold exercises the user could train from?
 *
 * The check matters most for the plan backups: the damaged plan was journalled just
 * like the healthy ones, so a backup can itself be empty. Treating one of those as a
 * recovery would "restore" the very damage being repaired. */
export function trainingStructureHasExercises(structure: TrainingProgramBackup | undefined): boolean {
  if (!structure?.workouts?.length || !structure.subSlots) {
    return false;
  }
  // a day whose id list is empty contributes nothing, and an id with no dictionary entry
  // behind it would come back as a blank exercise — neither counts as something to train
  for (const workout of structure.workouts) {
    for (const subSlotId of workout?.subSlotIds ?? []) {
      if (structure.subSlots[subSlotId]) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Turn a stored whole-plan backup document into a candidate, or answer `undefined` when
 * that backup is itself empty and so cannot repair anything.
 */
export function planBackupAsRecoveryCandidate(
  backup: TrainingProgramBackup | undefined,
  timestampUtc: string | undefined,
  source: PlanRecoverySource,
): PlanRecoveryCandidate | undefined {
  const structure = { workouts: backup?.workouts, subSlots: backup?.subSlots } as TrainingProgramBackup;
  if (!trainingStructureHasExercises(structure)) {
    return undefined;
  }
  return {
    source,
    timestampUtc,
    workouts: deepClone(structure.workouts),
    subSlots: deepClone(structure.subSlots),
  };
}

/**
 * Reassemble the training structure from the user's own logged workouts.
 *
 * What a workout log holds, and therefore what this can and cannot do: each
 * `Log_Workout` says which day of the plan it was (`planReference`) and, for every
 * exercise, carries `planSubslotBackup` — a complete `Plan_SubSlot` including all the
 * progression data. That copy is the workout's own copy of the plan, frozen against
 * later plan edits but written by that workout's own progression (#4531). So one logged
 * workout restores one training day completely, exercises and progression alike.
 *
 * It cannot restore a day the user has never trained, because nothing ever recorded one,
 * and it has no way to know how many days the plan is supposed to have — so a rebuild
 * from this source may be short of days and cannot tell that it is. `PlanRecoverySource`
 * is what says which source won, and the caller asks for a plan rebuild whenever this one
 * did, precisely because the result may be incomplete.
 *
 * Only the newest log per day is used, and a log is only usable when it has its own
 * saved `exercises` array. That array is read directly rather than through
 * `WorkoutLoaderService.getEffectiveExercises`, which derives missing exercises FROM the
 * plan — and the plan is the broken thing here.
 */
export function rebuildTrainingStructureFromWorkoutLogs(
  logWorkouts: Log_Workout[] | undefined,
): PlanRecoveryCandidate | undefined {
  // newest usable log per day, keyed by the day it belongs to
  const newestPerDay = new Map<number, { log: Log_Workout; at: string }>();
  for (const log of logWorkouts ?? []) {
    const day = log?.planReference;
    const at = comparableInstant(log?.time_utc);
    if (typeof day !== 'number' || !at) continue;
    // a log with no exercise carrying a plan copy rebuilds nothing
    if (!log.exercises?.some(exercise =>
      exercise?.subSlotId && exercise.planSubslotBackup && !isSplitSubSlotId(exercise.subSlotId))) continue;

    const held = newestPerDay.get(day);
    if (!held || held.at < at) {
      newestPerDay.set(day, { log, at });
    }
  }
  if (newestPerDay.size === 0) {
    return undefined;
  }

  // rebuild in day order, so the days come back as A, B, C rather than in whatever order
  // the logs were read
  const days = [...newestPerDay.keys()].sort((a, b) => a - b);
  const workouts: Plan_Workout[] = [];
  const subSlots: { [subSlotId: string]: Plan_SubSlot } = {};
  let newestTimestampUtc = '';

  for (const day of days) {
    const { log, at } = newestPerDay.get(day);
    const subSlotIds: string[] = [];
    for (const exercise of log.exercises) {
      if (!exercise?.subSlotId || !exercise.planSubslotBackup) continue;
      // an entry split off by an exercise change (#5507) holds the exercise the user changed
      // AWAY from; the program never held it under that id, so it is not part of the day
      if (isSplitSubSlotId(exercise.subSlotId)) continue;
      // the same exercise can be logged more than once in one workout; the day must
      // reference it once
      if (subSlotIds.includes(exercise.subSlotId)) continue;
      subSlotIds.push(exercise.subSlotId);
      subSlots[exercise.subSlotId] = deepClone(exercise.planSubslotBackup);
    }
    if (subSlotIds.length === 0) continue;
    // named by the position it ends up in, NOT by the day it used to be. Everywhere else
    // in the app a day's identity IS its index in this array — the letter shown to the
    // user is derived from that index — so a rebuild that skipped an untrained day would
    // otherwise produce a plan whose second day calls itself "C".
    workouts.push({ name: planWorkoutNameForDay(workouts.length), subSlotIds });
    if (at > newestTimestampUtc) {
      newestTimestampUtc = at;
    }
  }

  const candidate: PlanRecoveryCandidate = {
    source: PlanRecoverySource.WorkoutLogs,
    timestampUtc: newestTimestampUtc,
    workouts,
    subSlots,
  };
  return trainingStructureHasExercises(candidate) ? candidate : undefined;
}

/**
 * The plan's day names are the letters the user sees: day 0 is "A", day 1 is "B", and so
 * on. Past the alphabet the numbers are used instead, which no real plan reaches but
 * which keeps the name unique rather than silently colliding on "A".
 */
function planWorkoutNameForDay(day: number): string {
  return day >= 0 && day < 26 ? String.fromCharCode('A'.charCodeAt(0) + day) : String(day + 1);
}

/**
 * Choose which candidate to restore.
 *
 * The newest copy wins, because a plan backup written after the user's last workout
 * reflects edits the workout logs cannot know about, and a workout logged after the last
 * backup reflects exercises that backup predates.
 *
 * A candidate with no date sorts last. Only the plan's own `generatedPlanBackup` is in
 * that position: it carries no timestamp and reflects the last time the programme was
 * GENERATED rather than anything the user did afterwards, so it is the right last resort
 * and the wrong first choice.
 */
export function pickBestPlanRecoveryCandidate(
  candidates: (PlanRecoveryCandidate | undefined)[],
): PlanRecoveryCandidate | undefined {
  let best: PlanRecoveryCandidate | undefined;
  let bestAt: string | undefined;
  for (const candidate of candidates) {
    if (!candidate || !trainingStructureHasExercises(candidate)) continue;
    const at = comparableInstant(candidate.timestampUtc);
    if (!best) {
      best = candidate;
      bestAt = at;
      continue;
    }
    // an undated candidate never displaces a dated one, and two undated ones keep the
    // first offered, so the caller's order decides
    if (!at) continue;
    if (!bestAt || bestAt < at) {
      best = candidate;
      bestAt = at;
    }
  }
  return best;
}
